import { useState, useEffect } from 'react';
import observationFieldsService, { type ObservationField } from '../api/observationFieldsService';

/**
 * Wind field names that require m/s to km/h conversion for display
 */
const WIND_FIELD_NAMES = ['windSpeed', 'windGust'];

/**
 * Conversion factor from m/s to km/h
 */
const MS_TO_KMH = 3.6;

/**
 * Hook to fetch and cache observation field metadata from the observation_fields table.
 * This provides the master configuration for how observation readings are displayed in the UI.
 */
export function useObservationFields() {
  const [fields, setFields] = useState<ObservationField[]>([]);
  const [fieldsByName, setFieldsByName] = useState<Map<string, ObservationField>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    observationFieldsService.getAllObservationFields()
      .then(fetchedFields => {
        if (cancelled) return;
        setFields(fetchedFields);
        setFieldsByName(new Map(fetchedFields.map(f => [f.name, f])));
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to fetch observation fields:', err);
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Get visible fields (non-hidden and non-demo or demo-allowed based on context)
   */
  const getVisibleFields = (includeDemoOnly: boolean = false): ObservationField[] => {
    return fields.filter(f => !f.hidden && (includeDemoOnly || !f.demo_only));
  };

  /**
   * Get fields configured for Historical Weather Trend view
   */
  const getHistoricalTrendFields = (includeDemoOnly: boolean = false): ObservationField[] => {
    return fields
      .filter(f => !f.hidden && f.include_in_historical_trend && (includeDemoOnly || !f.demo_only))
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
  };

  /**
   * Get fields configured for Session Comparison view
   */
  const getSessionCompareFields = (includeDemoOnly: boolean = false): ObservationField[] => {
    return fields
      .filter(f => !f.hidden && f.include_in_session_compare && (includeDemoOnly || !f.demo_only))
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
  };

  /**
   * Get field metadata by name
   */
  const getField = (name: string): ObservationField | null => {
    return fieldsByName.get(name) || null;
  };

  /**
   * Check if a field requires unit conversion (m/s to km/h)
   */
  const requiresConversion = (name: string): boolean => {
    return WIND_FIELD_NAMES.includes(name);
  };

  /**
   * Convert a value for display (applies m/s to km/h for wind fields)
   * @param name - The field's name
   * @param value - The raw value from the database
   * @returns The converted value for display
   */
  const convertForDisplay = (name: string, value: number | null): number | null => {
    if (value === null) return null;
    return requiresConversion(name) ? value * MS_TO_KMH : value;
  };

  /**
   * Get the display unit for a field (returns km/h for wind fields, otherwise the stored unit)
   */
  const getDisplayUnit = (name: string): string | null => {
    const field = getField(name);
    if (!field) return null;
    // Wind fields are stored in m/s but displayed in km/h
    return requiresConversion(name) ? 'km/h' : field.unit;
  };

  /**
   * Infer unit from field name for unconfigured fields
   * Used when displaying data that isn't in the observation_fields table
   */
  const inferUnitFromName = (name: string): string | null => {
    const lower = name.toLowerCase();
    if (lower.includes('temp')) return '°C';
    if (lower.includes('wind') || lower.includes('gust')) return 'km/h'; // Display unit
    if (lower.includes('humidity')) return '%';
    if (lower.includes('pressure')) return 'hPa';
    if (lower.includes('rain') || lower.includes('precip')) return 'mm/h';
    if (lower.includes('direction')) return '°';
    return null;
  };

  /**
   * Create a default field for readings not in the observation_fields table
   * Infers unit from field name patterns for proper display
   */
  const createDefaultField = (name: string, unit?: string | null): ObservationField => ({
    name: name,
    display_name: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    unit: unit || inferUnitFromName(name),
    position: null,
    demo_only: false,
    hidden: false,
    include_in_historical_trend: false,
    include_in_session_compare: false,
  });

  return { 
    fields, 
    fieldsByName, 
    loading, 
    error,
    // Field access
    getField,
    getVisibleFields,
    getHistoricalTrendFields,
    getSessionCompareFields,
    createDefaultField,
    // Unit conversion utilities
    requiresConversion,
    convertForDisplay,
    getDisplayUnit,
  };
}
